import type { OnboardingStepProps } from "./Onboarding";
import type {
  AnnualRevenue,
  ProductDescription,
} from "./OnboardingShopInfoContent";

import React, { useEffect } from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { errorToast } from "@replo/design-system/components/alert/Toast";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import OnboardingFooter from "./OnboardingFooter";
import OnboardingShopInfoContent from "./OnboardingShopInfoContent";

const OnboardingShopInfo: React.FC<OnboardingStepProps> = ({
  currentStep,
  submitStep,
  isLoading,
  totalSteps,
}) => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const [shopName, setShopName] = useLocalStorageState(
    "onboarding-shopName",
    "",
    { schema: z.string() },
  );
  const [subdomain, setSubdomain] = useLocalStorageState(
    "onboarding-subdomain",
    "",
    { schema: z.string() },
  );
  const [productDescription, setProductDescription] = useLocalStorageState(
    "onboarding-productDescription",
    null,
    {
      schema: z
        .enum([
          "physical",
          "software",
          "digital",
          "membership",
          "courses",
          "professional",
          "events",
          "retail",
        ])
        .nullable(),
    },
  ) as [ProductDescription | null, (value: ProductDescription | null) => void];
  const [annualRevenue, setAnnualRevenue] = useLocalStorageState(
    "onboarding-annualRevenue",
    null,
    {
      schema: z
        .enum(["0-500k", "500k-2m", "2m-5m", "5m-10m", "10m-50m", "50m+"])
        .nullable(),
    },
  ) as [AnnualRevenue | null, (value: AnnualRevenue | null) => void];

  const [userFirstName] = useLocalStorageState("onboarding-firstName", "", {
    schema: z.string(),
  });
  const [userLastName] = useLocalStorageState("onboarding-lastName", "", {
    schema: z.string(),
  });
  const [teamDescription] = useLocalStorageState(
    "onboarding-teamDescription",
    null,
    { schema: z.enum(["business", "agency", "freelancer"]).nullable() },
  );

  const isBusinessContext = teamDescription === "business";

  const navigate = useNavigate();

  const { mutate: updateBasicShopInfo, isPending: isUpdatingBasicShopInfo } =
    trpc.onboarding.updateBasicShopInfo.useMutation();

  useEffect(() => {
    if (shopName) {
      const formattedSubdomain = shopName
        .toLowerCase()
        .replace(/[^\d\sa-z-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      setSubdomain(formattedSubdomain);
    } else {
      setSubdomain("");
    }
  }, [shopName, setSubdomain]);

  const mapRevenueToNumber = (revenue: string | null): number => {
    switch (revenue) {
      case "0-500k":
        return 0;
      case "500k-2m":
        return 500000;
      case "2m-5m":
        return 2000000;
      case "5m-10m":
        return 5000000;
      case "10m-50m":
        return 10000000;
      case "50m+":
        return 50000000;
      default:
        return 0;
    }
  };

  const handleSubmit = () => {
    const isFormValid =
      shopName.trim() !== "" &&
      subdomain.trim() !== "" &&
      productDescription !== null &&
      annualRevenue !== null &&
      userFirstName !== "" &&
      teamDescription !== null;

    if (isFormValid) {
      updateBasicShopInfo(
        {
          firstName: userFirstName,
          lastName: userLastName,
          shopName: shopName.trim(),
          teamDescription: teamDescription,
          productDescription: productDescription!,
          annualRevenue: mapRevenueToNumber(annualRevenue),
        },
        {
          onSuccess: () => {
            void trpcUtils.workspace.getUserWorkspacesList.invalidate();
            void trpcUtils.project.listWithStats.invalidate();
            void trpcUtils.user.get.invalidate();

            if (user) {
              logEvent("onboarding.flow.step.completed", {
                userId: user.email,
                stepId: "business-details",
                stepType: "business-details",
                stepData: {
                  shopName,
                  subdomain,
                  productDescription,
                  annualRevenue,
                },
              });
            }
            submitStep();
          },
          onError: () => {
            errorToast(
              "Unable to save information",
              "Please try again or contact support if the problem persists",
            );
          },
        },
      );
    } else {
      errorToast(
        "Missing required information",
        "Please fill out all required fields.",
      );
    }
  };

  const handleBack = () => {
    navigate("/onboarding/onboarding-user-info");
  };

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-12 md:gap-3">
          <h1 className="header-h1 text-3xl md:text-4xl">
            {isBusinessContext
              ? "Set Up Your Business"
              : "Fill Us In on Your Client"}
          </h1>
          <p className="text-muted header-h1 text-lg font-normal">
            We'll use this info to tailor reccommendations for you.
          </p>
        </div>
        <div className="lg:w-[1000px] w-full pb-25 md:pb-40 px-5 lg:px-0">
          <OnboardingShopInfoContent
            shopName={shopName}
            setShopName={setShopName}
            subdomain={subdomain}
            setSubdomain={setSubdomain}
            productDescription={productDescription}
            setProductDescription={(value) => setProductDescription(value)}
            annualRevenue={annualRevenue}
            setAnnualRevenue={(value) => setAnnualRevenue(value)}
            isBusinessContext={isBusinessContext}
          />
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20">
        <OnboardingFooter
          currentStep={currentStep}
          onNext={handleSubmit}
          onBack={handleBack}
          className="w-full lg:max-w-[1000px] px-5 lg:px-0"
          isNextLoading={isLoading || isUpdatingBasicShopInfo}
          isNextDisabled={
            isLoading ||
            isUpdatingBasicShopInfo ||
            shopName.trim() === "" ||
            subdomain.trim() === "" ||
            productDescription === null ||
            annualRevenue === null ||
            userFirstName === "" ||
            teamDescription === null
          }
          totalSteps={totalSteps}
        />
      </div>
    </div>
  );
};

export default OnboardingShopInfo;
